import { useCallback, useEffect, useState } from "react";
import { Server } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "../lib/api";
import { useRouters } from "../context/RouterContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { DatePicker } from "../components/DatePicker";

export default function Omset() {
  const { routers } = useRouters();
  const [data, setData] = useState({ days: [], totals: {}, per_router: [] });
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [cabang, setCabang] = useState("");

  const load = useCallback(() => {
    api.get("/reports/omset", { params: { date_from: from || undefined, date_to: to || undefined, router_id: cabang || undefined } })
      .then((r) => setData({ per_router: [], ...r.data }))
      .catch((e) => toast.error(errMsg(e)));
  }, [from, to, cabang]);

  useEffect(() => { load(); }, [load]);

  const t = data.totals || {};
  return (
    <div className="space-y-6" data-testid="omset-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Laporan Omset</h1>
          <p className="text-sm text-muted-foreground mt-1">Rekapitulasi pendapatan semua layanan (default: bulan berjalan)</p>
        </div>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="space-y-1.5">
            <Label>Cabang</Label>
            <Select value={cabang || "all"} onValueChange={(v) => setCabang(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44" data-testid="select-omset-cabang"><SelectValue placeholder="Semua Cabang" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Cabang</SelectItem>
                {routers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Dari</Label><DatePicker value={from} onChange={(v) => setFrom(v)} testid="input-omset-from" /></div>
          <div className="space-y-1.5"><Label>Sampai</Label><DatePicker value={to} onChange={(v) => setTo(v)} testid="input-omset-to" /></div>
          <Button variant="secondary" onClick={() => { setFrom(""); setTo(""); }} data-testid="btn-omset-reset">Reset</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "PPP", value: t.ppp, color: "text-sky-400", testid: "omset-ppp" },
          { label: "WiFi Hotspot", value: t.wifi, color: "text-cyan-400", testid: "omset-wifi" },
          { label: "Komisi Agen", value: t.komisi, color: "text-rose-400", testid: "omset-komisi" },
          { label: "Total Omset", value: t.total, color: "text-emerald-400", testid: "omset-total" },
        ].map((k) => (
          <Card key={k.label} className="bg-card/60 border-border">
            <CardContent className="p-4">
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{k.label}</div>
              <div className={`mt-1.5 text-lg font-extrabold font-mono ${k.color}`} data-testid={k.testid}>{rupiah(k.value)}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(data.per_router || []).length > 0 && (
        <Card className="bg-card/60 border-border" data-testid="omset-per-cabang">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Server className="w-4 h-4 text-sky-500" /> Rekap per Cabang</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[680px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Cabang</TableHead>
                  <TableHead className="text-right">Transaksi</TableHead>
                  <TableHead className="text-right">PPP</TableHead>
                  <TableHead className="text-right">WiFi</TableHead>
                  <TableHead className="text-right">Komisi</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-40">Porsi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.per_router.map((r) => {
                  const share = t.total ? Math.round((r.total / t.total) * 100) : 0;
                  return (
                    <TableRow key={r.router_id || "none"} data-testid={`omset-cabang-${r.router_id || "none"}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right font-mono">{r.count}</TableCell>
                      <TableCell className="text-right font-mono">{rupiah(r.ppp)}</TableCell>
                      <TableCell className="text-right font-mono">{rupiah(r.wifi)}</TableCell>
                      <TableCell className="text-right font-mono text-rose-400">{rupiah(r.komisi)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-400">{rupiah(r.total)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 rounded-full bg-accent overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${share}%` }} /></div>
                          <span className="text-[11px] font-mono text-muted-foreground w-9 text-right">{share}%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card/60 border-border" data-testid="omset-chart-card">
        <CardHeader><CardTitle className="text-base font-heading">Omset Harian per Layanan</CardTitle></CardHeader>
        <CardContent className="h-80">
          {data.days.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.days} margin={{ left: 8, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(8)} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}rb`} stroke="hsl(var(--muted-foreground))" />
                <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ppp" name="PPP" stackId="a" fill="#0284C7" radius={[0, 0, 0, 0]} />
                <Bar dataKey="wifi" name="WiFi" stackId="a" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="omset-empty">Belum ada data omset pada periode ini</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
